<template>
  <div class="tournament-timer" :class="{'mobile': isMobile}">
    <!-- Desktop version -->
    <div v-if="!isMobile" class="timer-desktop">
      <TournamentEndedLabel
        v-if="hasEnded"
        :tournament-end-date="tournamentEndDate"
      />
      <div v-else :class="'tournament-timer tournament-timer--' + timerState.type">
        <Fragment>
            <div :class="'d-flex timer-state timer-box' + ' ' + timerState.color" :style="timerStateStyle">
                <span class="d-flex">{{ timerState.text }}</span>
            </div>
            <div v-for="(el, i) in timerFormatted" :key="i">
                <span :class="'tournament-timer__number tournament-timer__' + el.indicates + ' timer-box'">
                    {{ el.value }}<br><span>{{ el.indicates }}</span>
                </span>
            </div>
        </Fragment>
      </div>
    </div>

    <!-- Mobile version -->
    <div v-else class="timer-mobile">
      <!-- Tournament status badge -->
      <div class="timer-status" :class="statusClass" :style="timerStatusStyle">
        <span class="status-dot" :style="statusDotStyle"></span>
        <span class="status-text">{{ statusText }}</span>
      </div>
      
      <!-- Timer display for in progress or upcoming tournaments -->
      <div v-if="tournamentIsActive && !competitionEndedLocally" class="timer-display">
        <div class="time-unit">
          <span class="time-value">{{ displayTime.days }}</span>
          <span class="time-label">{{ $t('days') }}</span>
        </div>
        <div class="timer-separator">:</div>
        <div class="time-unit">
          <span class="time-value">{{ displayTime.hours }}</span>
          <span class="time-label">{{ $t('hours') }}</span>
        </div>
        <div class="timer-separator">:</div>
        <div class="time-unit">
          <span class="time-value">{{ displayTime.minutes }}</span>
          <span class="time-label">{{ $t('min') }}</span>
        </div>
        <div class="timer-separator">:</div>
        <div class="time-unit">
          <span class="time-value">{{ displayTime.seconds }}</span>
          <span class="time-label">{{ $t('sec') }}</span>
        </div>
      </div>

      <!-- Date information -->
      <div class="timer-dates">
        <div class="date-info">
          <span class="date-label">{{ $t('starts') }}:</span>
          <span class="date-value">{{ formatDate(tournamentStartDate) }}</span>
        </div>
        <div class="date-separator"></div>
        <div class="date-info">
          <span class="date-label">{{ $t('ends') }}:</span>
          <span class="date-value">{{ formatDate(tournamentEndDate) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Fragment } from 'vue-fragment';
import TournamentEndedLabel from './TournamentEndedLabel.vue';

export default {
  name: 'TournamentTimer',
  props: {
    tournamentStartDate: {
      type: Date,
      required: true
    },
    tournamentEndDate: {
      type: Date,
      required: true
    },
    tournamentInProgress: {
      type: Boolean,
      default: false
    },
    tournamentIsActive: {
      type: Boolean,
      default: false
    },
    tournamentUserEnrolled: {
      type: Object,
      default: null
    }
  },
  components: {
    Fragment,
    TournamentEndedLabel
  },
  data() {
    return {
      timerState: { type: "box-type", text: "BEGINS IN", color: "bg-yellow" },
      timerFormatted: [{ value: "00", indicates: "day" }, { value: "00", indicates: "hour" }, { value: "00", indicates: "min" }, { value: "00", indicates: "sec" }],
      hasEnded: false,
      intervalId: null, // Almacena el ID del intervalo
      timeRemaining: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      timerInterval: null,
      isMobile: false,
      /** True when countdown to endTime reached 0 but parent may still pass tournamentInProgress (e.g. public embed). */
      competitionEndedLocally: false,
      /** True once the countdown had remaining time; avoids reload loop when page loads already at 0. */
      hadRemainingTime: false,
      countdownRefreshTriggered: false,
      scheduleInterval: null
    }
  },
  watch: {
    tournamentInProgress(val) {
      if (!val) {
        this.competitionEndedLocally = false;
      }
    },
    tournamentEndDate() {
      if (new Date() < this.tournamentEndDate) {
        this.competitionEndedLocally = false;
        this.hadRemainingTime = false;
      }
      this.startScheduleWatcher();
    },
    tournamentStartDate() {
      this.startScheduleWatcher();
    },
    "$store.state.tournament": {
      deep: true,
      immediate: true,
      handler() {
        this.setTimeForCard();
      }
    }
  },
  computed: {
    displayTime() {
      return {
        days: this.padZero(this.timeRemaining.days),
        hours: this.padZero(this.timeRemaining.hours),
        minutes: this.padZero(this.timeRemaining.minutes),
        seconds: this.padZero(this.timeRemaining.seconds)
      }
    },
    statusClass() {
      if (this.competitionEndedLocally || new Date() > this.tournamentEndDate) return 'closed';
      if (this.tournamentInProgress) return 'in-progress';

      const now = new Date();
      if (now < this.tournamentStartDate) return 'upcoming';

      return '';
    },
    statusText() {
      if (this.competitionEndedLocally || new Date() > this.tournamentEndDate) return this.$t('closed');
      if (this.tournamentInProgress) return this.$t('inProgress');

      const now = new Date();
      if (now < this.tournamentStartDate) return this.$t('upcoming');

      return '';
    },
    
    // Computed property for timer status styling with YIQ contrast
    timerStatusStyle() {
      const status = this.statusClass;
      let backgroundColor = '';
      let borderColor = '';
      
      switch (status) {
        case 'in-progress':
          backgroundColor = '00F784'; // Green
          borderColor = '00F784';
          break;
        case 'upcoming':
          backgroundColor = 'FFF75F'; // Yellow
          borderColor = 'FFE500';
          break;
        case 'closed':
          backgroundColor = '888888'; // Gray
          borderColor = '888888';
          break;
        default:
          backgroundColor = 'f8f8f8';
          borderColor = 'e0e0e0';
      }
      
      const textColor = this.getContrastYIQ(backgroundColor);
      
      return {
        color: textColor === 'black' ? '#000000' : '#ffffff',
        backgroundColor: `#${backgroundColor}33`, // Add transparency
        borderColor: `#${borderColor}`
      };
    },
    
    // Computed property for status dot styling
    statusDotStyle() {
      const status = this.statusClass;
      let dotColor = '';
      
      switch (status) {
        case 'in-progress':
          dotColor = '00F784'; // Green
          break;
        case 'upcoming':
          dotColor = 'FFE500'; // Yellow
          break;
        case 'closed':
          dotColor = '888888'; // Gray
          break;
        default:
          dotColor = 'e0e0e0';
      }
      
      return {
        backgroundColor: `#${dotColor}`
      };
    },
    
    // Computed property for desktop timer state styling with YIQ contrast
    timerStateStyle() {
      const colorClass = this.timerState.color;
      let backgroundColor = '';
      
      switch (colorClass) {
        case 'bg-green':
          backgroundColor = '00F784'; // Green
          break;
        case 'bg-yellow':
          backgroundColor = 'FFF75F'; // Yellow
          break;
        case 'bg-orange':
          backgroundColor = 'FF8933'; // Orange
          break;
        case 'bg-red':
          backgroundColor = 'C42A14'; // Red
          break;
        default:
          backgroundColor = 'EEEEEE'; // Light gray
      }
      
      const textColor = this.getContrastYIQ(backgroundColor);
      
      return {
        color: textColor === 'black' ? '#000000' : '#ffffff'
      };
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    this.startTimer();
    this.startScheduleWatcher();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
    clearInterval(this.timerInterval);
    if (this.scheduleInterval) {
      clearInterval(this.scheduleInterval);
    }
  },
  unmounted() {
    if (this.intervalId) {
      clearInterval(this.intervalId); // Elimina el intervalo cuando el componente se desmonte
    }
  },
  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.scheduleInterval) {
      clearInterval(this.scheduleInterval);
    }
  },
  methods: {
    reloadPage() {
      if (this.countdownRefreshTriggered) return;
      this.countdownRefreshTriggered = true;
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      if (this.scheduleInterval) {
        clearInterval(this.scheduleInterval);
        this.scheduleInterval = null;
      }
      window.location.reload();
    },
    refreshOnCountdownEnd() {
      if (!this.hadRemainingTime) return;
      this.reloadPage();
    },
    startScheduleWatcher() {
      if (this.scheduleInterval) {
        clearInterval(this.scheduleInterval);
        this.scheduleInterval = null;
      }

      let watchedBeforeStart = false;
      let watchedBeforeEnd = false;

      const tick = () => {
        const startMs = this.tournamentStartDate.getTime();
        const endMs = this.tournamentEndDate.getTime();
        if (Number.isNaN(startMs) || Number.isNaN(endMs)) return;

        const now = Date.now();
        const toStart = startMs - now;
        const toEnd = endMs - now;

        if (toStart > 0) watchedBeforeStart = true;
        if (now >= startMs && toEnd > 0) watchedBeforeEnd = true;

        if (watchedBeforeStart && toStart <= 0 && now < endMs) {
          this.reloadPage();
          return;
        }

        if (watchedBeforeEnd && toEnd <= 0) {
          this.reloadPage();
        }
      };

      tick();
      this.scheduleInterval = setInterval(tick, 1000);
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 600;
    },
    startTimer() {
      this.calculateTimeRemaining();
      
      this.timerInterval = setInterval(() => {
        this.calculateTimeRemaining();
      }, 1000);
    },
    calculateTimeRemaining() {
      const now = new Date();
      let targetDate;
      
      if (this.tournamentInProgress) {
        // If in progress, count down to end date
        targetDate = this.tournamentEndDate;
      } else if (now < this.tournamentStartDate) {
        // If not started, count down to start date
        targetDate = this.tournamentStartDate;
      } else {
        clearInterval(this.timerInterval);
        if (this.hadRemainingTime) {
          this.refreshOnCountdownEnd();
        }
        return;
      }
      
      const difference = targetDate - now;
      
      if (difference <= 0) {
        clearInterval(this.timerInterval);
        this.timeRemaining = {days: 0, hours: 0, minutes: 0, seconds: 0};
        if (this.hadRemainingTime) {
          this.refreshOnCountdownEnd();
          return;
        }
        if (this.tournamentInProgress) {
          this.competitionEndedLocally = true;
        }
        return;
      }

      this.hadRemainingTime = true;
      this.timeRemaining = {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      };
    },
    padZero(num) {
      return num.toString().padStart(2, '0');
    },
    formatDate(date) {
      const options = { month: 'short', day: 'numeric', year: 'numeric' };
      return date.toLocaleDateString(this.$i18n.locale, options);
    },
    setTimeForCard() {
      // Clear any existing interval before setting a new one
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      let self = this;
      if (this.tournamentIsActive) {
        this.hasEnded = false;
        let compareWithDate = undefined;
        if (this.tournamentInProgress) {
          this.timerState = {
            text: this.$t("endsIn"),
            color: this.tournamentUserEnrolled ? "bg-green" : "bg-orange"
          }
          compareWithDate = this.tournamentEndDate;
        } else {
          this.timerState = {
            text: this.tournamentUserEnrolled ? this.$t("beginsIn") : this.$t("stillOpen"),
            color: "bg-yellow"
          }
          compareWithDate = this.tournamentStartDate;
        }

        this.timerState.text = this.timerState.text.toUpperCase();

        const tickCountdown = () => {
          const diffMs = compareWithDate - new Date();
          if (diffMs > 0) {
            self.hadRemainingTime = true;
            const remainingTime = self.dhm(diffMs).split(":");
            self.timerFormatted = [{
              value: Number(remainingTime[0]).toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
              }), indicates: this.$t("day")
            }, {
              value: Number(remainingTime[1]).toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
              }), indicates: this.$t("hour")
            }, {
              value: Number(remainingTime[2]).toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
              }), indicates: this.$t("minutes")
            }, {
              value: Number(remainingTime[3]).toLocaleString('en-US', {
                minimumIntegerDigits: 2,
                useGrouping: false
              }), indicates: this.$t("seconds")
            }];
            return;
          }
          clearInterval(self.intervalId);
          self.intervalId = null;
          if (self.hadRemainingTime) {
            self.refreshOnCountdownEnd();
            return;
          }
          if (self.tournamentInProgress) {
            self.competitionEndedLocally = true;
            self.hasEnded = true;
            self.timerState.text = self.$t("tournamentEnded");
          }
        };

        tickCountdown();
        this.intervalId = setInterval(tickCountdown, 1000);

      } else {
        if (this.hadRemainingTime) {
          this.refreshOnCountdownEnd();
          return;
        }
        this.timerState.text = this.$t("tournamentEnded");
        this.hasEnded = true;
      }
    },
    dhm(ms) {
      const safeMs = Math.max(0, ms);
      const days = Math.floor(safeMs / (24 * 60 * 60 * 1000));
      const daysms = safeMs % (24 * 60 * 60 * 1000);
      const hours = Math.floor(daysms / (60 * 60 * 1000));
      const hoursms = safeMs % (60 * 60 * 1000);
      const minutes = Math.floor(hoursms / (60 * 1000));
      const minutesms = safeMs % (60 * 1000);
      const sec = Math.floor(minutesms / 1000);
      return days + ":" + hours + ":" + minutes + ":" + sec;
    },
    
    // Helper method to determine if text should be black or white based on background color
    getContrastYIQ(hexcolor) {
      if (!hexcolor) return 'black';

      // Remove hash if it exists
      hexcolor = hexcolor.replace('#', '');

      const r = parseInt(hexcolor.substr(0, 2), 16);
      const g = parseInt(hexcolor.substr(2, 2), 16);
      const b = parseInt(hexcolor.substr(4, 2), 16);

      const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

      return (yiq >= 128) ? 'black' : 'white';
    }
  }
}
</script>



<style lang="scss" scoped>
$swt-success: var(--v-mainGreen-base);
$swt-error: #C42A14;
$swt-warning: #FF8933;
$swt-yellow: #FFF75F;
$swt-light-gray: #EEEEEE;
$swt-dark-gray: #303740;

.theme--dark {

  .tournament-timer {

    .timer-box {
      background-color: $swt-dark-gray;
    }

    .timer-box {
      &.bg-yellow {
        background-color: #FFC123;
      }
    }

    .tournament-timer__number {
      color: #fff;
    }
  }
}

.tournament-timer {
  display: flex;
  font-family: 'Avenir', sans-serif;

  &.tournament-timer--box-type {
    justify-content: space-between;
    flex-wrap: nowrap;
  }

  .timer-box {
    border-radius: 5px;
    background: $swt-light-gray;
    font-size: 14px;
    margin: 10px 6px;

    &.bg-green {
      background-color: $swt-success;
    }

    &.bg-red {
      background-color: $swt-error;
    }

    &.bg-yellow {
      background-color: $swt-yellow;
    }

    &.bg-orange {
      background: $swt-warning;
    }

    &.timer-state {
      font-weight: 500;
      font-family: 'Aeonik', 'Avenir', sans-serif;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 4px 11px;
      overflow: hidden;
      margin-left: 0;
      margin-right: 0;
      span {
        font-size: 14px;
        font-weight: 500;
        line-clamp: 2;
        text-align: center;
        max-width: 56px;
        overflow: hidden;
        text-overflow: ellipsis;
        color: #3B3B3B;
      }
    }

    &:last-of-type {
      margin-right: 0;
    }
  }

  /* Figma timer: big Aeonik number over a small unit label */
  .tournament-timer__number {
    display: flex;
    flex-direction: column;
    text-align: center;
    justify-content: center;
    font-family: 'Aeonik', 'Avenir', sans-serif;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.2;
    color: #3B3B3B;
    width: 61px;
    padding: 4px 11px;
    span {
      font-family: 'Avenir', sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 1.5;
    }
  }

  /*** Mobile Timer Styles ***/
  &.mobile .timer-mobile {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 15px;
    border-radius: 12px;
    background-color: var(--v-fullWhite-base);
    margin: 5px 0 15px;
    justify-content: center;
    
    /* Status badge */
    .timer-status {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 4px 12px;
      border-radius: 20px;
      align-self: center;
      font-size: 12px;
      font-weight: 500;
      margin-bottom: 12px;
      border: 1px solid;
      
      .status-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        animation: pulse 2s infinite;
      }
    }
    
    /* Timer display */
    .timer-display {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      margin-bottom: 12px;
      
      .time-unit {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 50px;
        
        .time-value {
          font-size: 24px;
          font-weight: 700;
          color: var(--v-mainBlack-base);
          font-variant-numeric: tabular-nums;
          letter-spacing: -0.5px;
          line-height: 1;
        }
        
        .time-label {
          font-size: 10px;
          color: #888;
          margin-top: 2px;
        }
      }
      
      .timer-separator {
        font-size: 22px;
        font-weight: 500;
        color: #888;
        margin-top: -4px;
      }
    }
    
    /* Date information */
    .timer-dates {
      display: flex;
      width: 100%;
      justify-content: space-around;
      align-items: center;
      padding-top: 12px;
      
      .date-info {
        display: flex;
        flex-direction: column;
        align-items: center;
        
        .date-label {
          font-size: 11px;
          color: #888;
          margin-bottom: 2px;
        }
        
        .date-value {
          font-size: 13px;
          font-weight: 500;
          color: var(--v-mainBlack-base);
        }
      }
      
      .date-separator {
        height: 25px;
        width: 1px;
        margin: 0 12px;
        background-color: #e0e0e0;
      }
    }
  }
  
  /* Animation for the status dot */
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
}
</style>
